import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtUSD, fmtDate, fmtCarat } from "@/lib/format";
import { toast } from "sonner";
import { TrendingUp, TrendingDown } from "lucide-react";
import { TableSummaryRow } from "@/components/TableSummaryRow";

export default function Sales() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try { const { data } = await api.get("/sales"); setRows(data); }
        catch { toast.error("Failed to load"); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    return (
        <div className="space-y-6 animate-fade-up" data-testid="sales-page">
            <header>
                <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">Revenue</div>
                <h1 className="font-display text-4xl sm:text-5xl leading-none tracking-tight">Sales</h1>
                <p className="text-sm text-stone-500 mt-2">Sales are recorded from a parcel (sell as-is) or a jewelry piece. Each sale executes the 3-gate waterfall automatically.</p>
            </header>

            {loading ? (
                <div className="text-stone-500 text-sm">Loading…</div>
            ) : rows.length === 0 ? (
                <div className="border border-stone-200 p-10 text-center bg-[#f5efe0]">
                    <TrendingUp className="mx-auto h-8 w-8 text-stone-500" strokeWidth={1.2} />
                    <h3 className="font-display text-xl mt-4">No sales yet</h3>
                </div>
            ) : (
                <div className="border border-stone-200 overflow-x-auto" data-testid="sales-table">
                    <table className="w-full text-sm min-w-[1300px]">
                        <thead>
                            <tr className="border-b border-stone-200 text-[10px] uppercase tracking-widest text-stone-500 text-left">
                                <th className="py-3 px-4">Date</th>
                                <th className="py-3 px-4">Type</th>
                                <th className="py-3 px-4">Target</th>
                                <th className="py-3 px-4 text-right">Carat</th>
                                <th className="py-3 px-4 text-right">Cost</th>
                                <th className="py-3 px-4">Buyer</th>
                                <th className="py-3 px-4">Received by</th>
                                <th className="py-3 px-4 text-right">Revenue</th>
                                <th className="py-3 px-4 text-right" title="Amount from sale proceeds that went to pay off the parcel's unpaid supplier balance. Zero for parcels that were fully paid before the sale.">To supplier</th>
                                <th className="py-3 px-4 text-right">Net profit</th>
                                <th className="py-3 px-4 text-right">Bashari owed</th>
                                <th className="py-3 px-4 text-right">Ido owed</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(s => {
                                const rb = s.received_by;
                                return (
                                    <tr key={s.id} className="border-b border-stone-200/70 hover:bg-stone-100" data-testid={`sale-row-${s.id}`}>
                                        <td className="py-3 px-4 text-stone-500">{fmtDate(s.sale_date)}</td>
                                        <td className="py-3 px-4 text-stone-500 text-[10px] uppercase tracking-widest">{s.target_type}</td>
                                        <td className="py-3 px-4">
                                            <Link to={s.target_type === "parcel" ? `/parcels/${s.target_id}` : `/jewelry/${s.target_id}`} className="text-stone-800 hover:text-emerald-700 font-mono text-xs">
                                                {s.target_label}
                                            </Link>
                                        </td>
                                        <td className="py-3 px-4 text-right num text-stone-700" data-testid={`sale-carat-${s.id}`}>
                                            {s.target_carat != null ? fmtCarat(s.target_carat) : "—"}
                                        </td>
                                        <td className="py-3 px-4 text-right num text-stone-700" data-testid={`sale-cost-${s.id}`}>
                                            {s.target_cost != null ? fmtUSD(s.target_cost) : "—"}
                                        </td>
                                        <td className="py-3 px-4" data-testid={`sale-buyer-cell-${s.id}`}>
                                            <span className="text-stone-700">{s.buyer || "—"}</span>
                                        </td>
                                        <td className="py-3 px-4" data-testid={`sale-received-by-cell-${s.id}`}>
                                            <span className={`px-2 py-0.5 text-[11px] uppercase tracking-widest ${rb === "bashari" ? "bg-amber-100 text-amber-800" : rb === "ido" ? "bg-sky-100 text-sky-800" : "bg-stone-100 text-stone-500"}`}>
                                                {rb === "bashari" ? "Bashari" : rb === "ido" ? "Ido" : "—"}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4 text-right num text-stone-900">{fmtUSD(s.sale_price)}</td>
                                        <td className="py-3 px-4 text-right num text-rose-600">{s.breakdown?.supplier_payment > 0.01 ? fmtUSD(s.breakdown.supplier_payment) : "—"}</td>
                                        <td className={`py-3 px-4 text-right num ${s.breakdown?.is_loss ? "text-rose-600" : "text-emerald-700"}`}>
                                            {s.breakdown?.is_loss
                                                ? <span className="inline-flex items-center gap-1"><TrendingDown className="h-3 w-3" /> {fmtUSD(-1 * (s.breakdown?.shortfall || 0))}</span>
                                                : fmtUSD(s.breakdown?.net_profit || 0)}
                                        </td>
                                        <td className="py-3 px-4 text-right num">{fmtUSD(s.breakdown?.total_payout?.bashari || 0)}</td>
                                        <td className="py-3 px-4 text-right num">{fmtUSD(s.breakdown?.total_payout?.ido || 0)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <TableSummaryRow
                            rows={rows}
                            testid="sales-summary-row"
                            cells={[
                                { type: "label", text: `${rows.length} rows` },
                                null, null,
                                { type: "sum-carat", get: r => r.target_carat, testid: "sales-summary-carat" },
                                { type: "sum-usd", get: r => r.target_cost, testid: "sales-summary-cost" },
                                null, null,
                                { type: "sum-usd", get: r => r.sale_price, testid: "sales-summary-revenue" },
                                { type: "sum-usd", get: r => r.breakdown?.supplier_payment, testid: "sales-summary-supplier" },
                                { type: "sum-usd", get: r => r.breakdown?.is_loss ? -(r.breakdown?.shortfall || 0) : (r.breakdown?.net_profit || 0), testid: "sales-summary-profit" },
                                { type: "sum-usd", get: r => r.breakdown?.total_payout?.bashari, testid: "sales-summary-bashari" },
                                { type: "sum-usd", get: r => r.breakdown?.total_payout?.ido, testid: "sales-summary-ido" },
                            ]}
                        />
                    </table>
                </div>
            )}
        </div>
    );
}
